import type { SessionInfo } from './types';

/**
 * The last identity the server actually confirmed.
 *
 * This is what lets the installed app keep working offline, and it is
 * deliberately a *grace period*, not a bypass: it records when the server last
 * verified this person, the app re-checks the moment it is online again, and
 * access lapses entirely once the window expires. Someone whose account is
 * disabled therefore loses access as soon as their device has connectivity, and
 * within the window at the latest.
 *
 * It grants nothing on its own — it cannot reach a document or convert a file,
 * both of which are local or need a server that will reject them.
 */

const KEY = 'pdf-toolkit-last-session';

/** How long offline work is allowed before the server must confirm again. */
const GRACE_MS = 7 * 24 * 60 * 60 * 1000;

interface Cached {
  session: SessionInfo;
  verifiedAt: number;
}

export function rememberSession(session: SessionInfo): void {
  try {
    localStorage.setItem(KEY, JSON.stringify({ session, verifiedAt: Date.now() } satisfies Cached));
  } catch {
    // Blocked storage: offline use simply will not be available.
  }
}

export function cachedSession(): Cached | null {
  try {
    const raw = localStorage.getItem(KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw) as Partial<Cached>;
    if (!parsed?.session?.user || typeof parsed.verifiedAt !== 'number') return null;
    if (Date.now() - parsed.verifiedAt > GRACE_MS) {
      forgetSession();
      return null;
    }
    return parsed as Cached;
  } catch {
    return null;
  }
}

export function forgetSession(): void {
  try {
    localStorage.removeItem(KEY);
  } catch {
    // Nothing to do.
  }
}

export function graceRemainingDays(verifiedAt: number): number {
  return Math.max(0, Math.ceil((GRACE_MS - (Date.now() - verifiedAt)) / (24 * 60 * 60 * 1000)));
}
